import { FC, useRef } from "react";

import { useFormikContext } from "formik";

import Select from "@components/Select";

import loadLockers from "./services/loadLockers";
import type { TLockerEntry } from "./types";
import mapLockersToOptions from "./utils/mapLockersToOptions";

type TLockerInputProps = {
  countryCode: string;
  lockerCodeFieldName?: string;
  lockerNameFieldName?: string;
  lockerPostalCodeFieldName?: string;
  lockerStreetName?: string;
  lockerCityName?: string;
  label: string;
  id: string;
  isDisabled?: boolean;
  onSelect?: (locker?: TLockerEntry) => void;
  onError?: (error?: string) => void;
};

const LockerInput: FC<TLockerInputProps> = ({
  countryCode,
  lockerCodeFieldName,
  lockerNameFieldName,
  lockerPostalCodeFieldName,
  lockerStreetName,
  lockerCityName,
  label,
  id,
  isDisabled,
  onSelect,
  onError,
}) => {
  const optionsLockersRef = useRef<TLockerEntry[]>([]);

  const { values, setValues, errors, touched, setFieldTouched } = useFormikContext<any>();

  const lockerNameValue = lockerNameFieldName && values[lockerNameFieldName];
  const lockerCodeValue = lockerCodeFieldName && values[lockerCodeFieldName];

  const defaultOption = lockerCodeValue ? { label: lockerNameValue, value: lockerCodeValue } : undefined;

  const handleLoadLockers = async (query) => {
    const lockers = await loadLockers(countryCode, query, onError);
    optionsLockersRef.current = lockers;

    return mapLockersToOptions(lockers);
  };

  const handleLockerChanged = (lockerId: any) => {
    const selectedLocker = (optionsLockersRef.current || []).find(
      ({ id: optionLockerId }) => optionLockerId === lockerId
    );

    setValues((oldValues) => ({
      ...oldValues,
      ...(lockerCodeFieldName && { [lockerCodeFieldName]: selectedLocker?.id || null }),
      ...(lockerNameFieldName && { [lockerNameFieldName]: selectedLocker?.name || "" }),
      ...(lockerPostalCodeFieldName && { [lockerPostalCodeFieldName]: selectedLocker?.postalCode || "" }),
      ...(lockerStreetName && { [lockerStreetName]: selectedLocker?.street || "" }),
      ...(lockerCityName && { [lockerCityName]: selectedLocker?.city || "" }),
    }));

    if (onSelect) {
      onSelect(selectedLocker);
    }
  };

  const handleSelectBlur = () => {
    if (lockerCodeFieldName) {
      setFieldTouched(lockerCodeFieldName, true);
    }
  };

  const isInvalid = lockerCodeFieldName ? !!errors[lockerCodeFieldName] && !!touched[lockerCodeFieldName] : undefined;
  const message = lockerCodeFieldName ? (errors[lockerCodeFieldName] as string) : undefined;

  return (
    <Select
      data-field={lockerCodeFieldName}
      isFormik={false}
      label={label}
      id={id}
      onBlur={handleSelectBlur}
      loadOptions={handleLoadLockers}
      isDisabled={isDisabled}
      onChange={handleLockerChanged}
      value={lockerCodeValue}
      defaultOption={defaultOption}
      isInvalid={isInvalid}
      message={message}
      mode="asyncOption"
    />
  );
};
export default LockerInput;
